/**
 * DataBest — Dashboard page (F5).
 * Real KPIs + 4-axis radar + trend line + grouped breakdown.
 * Responsive: mobile stacked, tablet 2-col, desktop 12-col grid.
 */
import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
    Activity, Star, ThumbsUp, Users, Loader2,
} from "lucide-react";
import useDashboardData from "@/features/dashboard/hooks/useDashboardData";
import DashboardFilters from "@/features/dashboard/components/DashboardFilters";
import DashboardRadar from "@/features/dashboard/components/DashboardRadar";
import DashboardTrend from "@/features/dashboard/components/DashboardTrend";
import BreakdownTable from "@/features/dashboard/components/BreakdownTable";
import KpiCard from "@/features/dashboard/components/KpiCard";

const isoDaysAgo = (n) => {
    const d = new Date();
    d.setDate(d.getDate() - n);
    return d.toISOString();
};

const defaultFilters = () => ({
    from: isoDaysAgo(30),
    to: new Date().toISOString(),
    preset: "30d",
    individual_id: null,
    life_area: null,
    granularity: "day",
    group_by: "individual",
});

const DashboardPage = () => {
    const [filters, setFilters] = useState(defaultFilters);
    const { kpis, radar, trend, breakdown, loading } = useDashboardData(filters);

    const update = (patch) => setFilters((f) => ({ ...f, ...patch }));

    const rangeLabel = useMemo(() => {
        const label = filters.preset ? filters.preset.toUpperCase() : "custom";
        return `Last ${label}`;
    }, [filters.preset]);

    return (
        <div className="mx-auto max-w-7xl" data-testid="dashboard-page">
            <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35 }}
            >
                <p className="text-xs font-semibold uppercase tracking-[0.22em] text-brand-blue-700">
                    Dashboard
                </p>
                <h1 className="mt-1 font-heading text-3xl font-bold text-brand-blue-950 sm:text-4xl">
                    Insights across your programs
                </h1>
                <p className="mt-1 text-sm text-slate-500">
                    Aggregate view of scoring across dimensions, individuals and outcomes.
                </p>

                {/* Filters */}
                <div className="mt-5">
                    <DashboardFilters filters={filters} onChange={update} />
                </div>

                {/* KPI cards */}
                <div className="mt-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
                    <KpiCard
                        icon={Activity}
                        tone="blue"
                        label={rangeLabel}
                        value={kpis?.total_measurements ?? "—"}
                        hint="Scored measurements"
                        testId="kpi-total"
                        loading={loading}
                    />
                    <KpiCard
                        icon={Star}
                        tone="green"
                        label="Avg Plan Score"
                        value={
                            kpis?.avg_plan_score != null
                                ? kpis.avg_plan_score.toFixed(2)
                                : "—"
                        }
                        hint={
                            kpis?.avg_plan_letter_grade
                                ? `${kpis.avg_plan_letter_grade} · GPA ${kpis.avg_plan_gpa?.toFixed(1)}${kpis.avg_plan_qualitative ? " · " + kpis.avg_plan_qualitative : ""}`
                                : "(%D + %Q + (100−%Dep)) / 3"
                        }
                        testId="kpi-avg"
                        loading={loading}
                    />
                    <KpiCard
                        icon={ThumbsUp}
                        tone="amber"
                        label="Yes to supports"
                        value={
                            kpis?.yes_to_supports_pct != null
                                ? `${kpis.yes_to_supports_pct}%`
                                : "—"
                        }
                        hint="Response yes rate"
                        testId="kpi-yes"
                        loading={loading}
                    />
                    <KpiCard
                        icon={Users}
                        tone="violet"
                        label="Individuals"
                        value={kpis?.active_individuals ?? "—"}
                        hint="With measurements"
                        testId="kpi-individuals"
                        loading={loading}
                    />
                </div>

                {/* Radar + Trend */}
                <div className="mt-5 grid gap-4 lg:grid-cols-2">
                    <DashboardRadar radar={radar} loading={loading} />
                    <DashboardTrend trend={trend} loading={loading} />
                </div>

                {/* Breakdown */}
                <div className="mt-5">
                    <BreakdownTable
                        breakdown={breakdown}
                        groupBy={filters.group_by}
                        onGroupChange={(gb) => update({ group_by: gb })}
                        loading={loading}
                    />
                </div>

                {loading && (
                    <div
                        className="pointer-events-none fixed bottom-4 right-4 flex items-center gap-2 rounded-full border border-slate-200 bg-white/95 px-3 py-1.5 text-xs font-semibold text-slate-600 shadow-lg"
                        data-testid="dashboard-loading"
                    >
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        Updating…
                    </div>
                )}
            </motion.div>
        </div>
    );
};

export default DashboardPage;
